import type { ReactNode } from 'react';
import { Search, Store } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { GroupStoreStamp } from '@/hooks/useGroupAssets';

export const ALL_STORES = '__all__';

/**
 * The shared furniture for a chain-wide page — the header, the stat tiles and
 * the store/search filter row.
 *
 * ⚠ These pages NEVER switch the active account. That is the whole point of
 * them: an AP or ops person standing on corporate should be able to read the
 * chain without being moved onto one store, which is exactly the trap the
 * invoice list fell into. Anything that needs a store's own context links out
 * explicitly and says so.
 */

export function ChainPage({
  title,
  intro,
  children,
}: {
  title: string;
  intro: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-16 md:pb-10">
      <div className="px-6 md:px-8 pt-8 pb-12 max-w-6xl mx-auto">
        <h1 className="mb-1 text-center font-display text-2xl font-bold tracking-tight text-brix-ink">
          {title}
        </h1>
        <p className="text-center text-base mb-7 text-brix-muted max-w-2xl mx-auto">{intro}</p>
        {children}
      </div>
    </div>
  );
}

export function Tile({
  label,
  value,
  sub,
  tone = 'default',
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'default' | 'warning' | 'muted';
}) {
  return (
    <div className="rounded-2xl border border-brix-border bg-white px-4 py-3.5">
      <p className="text-[11px] uppercase tracking-wide text-brix-muted font-semibold">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-xl font-bold tabular-nums',
          tone === 'warning' ? 'text-brix-warning' : 'text-brix-ink',
        )}
      >
        {value}
      </p>
      {sub && <p className="text-[11px] text-brix-muted mt-0.5">{sub}</p>}
    </div>
  );
}

export function StoreFilterRow({
  stores,
  store,
  onStore,
  term,
  onTerm,
  searchPlaceholder,
  children,
}: {
  stores: GroupStoreStamp[];
  store: string;
  onStore: (v: string) => void;
  term: string;
  onTerm: (v: string) => void;
  searchPlaceholder: string;
  /** Optional filter pills rendered on the left. */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
      <div className="flex gap-2 flex-wrap">{children}</div>
      <div className="flex-1" />
      <div className="flex items-center gap-2">
        <div className="relative">
          <Store size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" />
          <select
            value={store}
            onChange={(e) => onStore(e.target.value)}
            aria-label="Filter by store"
            className="pl-8 pr-3 py-2.5 rounded-xl border border-brix-border bg-white text-sm text-brix-ink max-w-[15rem]"
          >
            <option value={ALL_STORES}>All stores</option>
            {stores.map((s) => (
              <option key={s.customer_id} value={s.customer_id}>
                {s.store_code ? `${s.store_code} · ${s.store_name}` : s.store_name}
              </option>
            ))}
          </select>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" />
          <input
            value={term}
            onChange={(e) => onTerm(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="pl-8 pr-3 py-2.5 rounded-xl border border-brix-border bg-white text-sm text-brix-ink w-[13rem]"
          />
        </div>
      </div>
    </div>
  );
}

export function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-5 py-2 text-sm rounded-full cursor-pointer transition-all duration-200',
        active
          ? 'bg-brix-primary-tint text-brix-primary font-semibold shadow-card'
          : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
      )}
    >
      {children}
    </button>
  );
}

/** The Store cell every chain table shares. */
export function StoreCell({ row }: { row: GroupStoreStamp }) {
  return (
    <td className="px-4 py-3 text-brix-ink">
      <span className="block">{row.store_name}</span>
      {row.store_code && (
        <span className="block text-[11px] text-brix-muted">#{row.store_code}</span>
      )}
    </td>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-brix-muted">
        {children}
      </td>
    </tr>
  );
}
